import React, { useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { refreshActiveTable } from '@/store/reducers/table-slice';
import { getUi, hideModal } from '@/store/reducers/ui-slice';
import { MethodResponse } from '@alien-platform/cms-bo';
import { IUserPermissions } from '@ap-db-worker/common';
import { Button, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import { CustomConfigInputProps, InputType } from '@/types/general';
import { CustomConfigInputV2 } from '@/components/core/custom-config-input-v2';

function ChangePasswordModalContent(): React.JSX.Element {
  const { modal } = useAppSelector(getUi);

  const [dto, setDto] = useState({
    password: '',
    confirmPassword: '',
  });

  const content = modal?.content as {
    opId: string;
    userId: string;
    nickname: string;
  };

  const inputs: CustomConfigInputProps[] = [
    {
      name: 'password',
      label: 'New Password',
      required: true,
      type: InputType.INPUT,
      value: dto?.password,
    },
    {
      name: 'confirmPassword',
      label: 'Confirm Password',
      required: true,
      type: InputType.INPUT,
      value: dto?.confirmPassword,
    },
  ];

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const handleClose = (): void => {
    dispatch(hideModal());
  };

  const handleSubmit = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await fetch(`/api/users/change-password`, {
        method: 'PUT',
        body: JSON.stringify({ opId: content?.opId, userId: content?.userId, ...dto }),
      });
      const result: MethodResponse<IUserPermissions> = await response.json();
      if (response.ok && result.success) {
        toast.success('Password changed successfully!');
        dispatch(hideModal());
        dispatch(refreshActiveTable());
      } else {
        toast.error(result.messages?.[0] || 'Error: 500 | Unable to change password');
      }
    } catch (e) {
      toast.error('Error: 500 | Unable to change password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <Typography variant="body2">
          Are you sure you want to change this user <b>{content?.nickname}</b> password?
        </Typography>

        {inputs.map((item, index) => (
          <CustomConfigInputV2 key={index} defaultData={dto} setDefaultData={setDto} {...item} />
        ))}
      </Stack>
      <Stack direction="row" spacing={3} sx={{ mt: 3, justifyContent: 'initial' }}>
        <Button type="button" color="secondary" onClick={handleClose} sx={{ flex: '1 1 auto' }}>
          Close
        </Button>

        <Button loading={isLoading} type="submit" color="error" sx={{ flex: '1 1 auto' }} variant="contained">
          Change Password
        </Button>
      </Stack>
    </form>
  );
}

export default ChangePasswordModalContent;
